import React, { useEffect, useState, useRef } from "react";
import clsx from "clsx";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const ChevronDownIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const QueueSelect = ({ selectedQueueIds = [], onChange }) => {
  const [queues, setQueues] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queue");
        setQueues(data);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleQueue = (queueId) => {
    if (selectedQueueIds.includes(queueId)) {
      onChange(selectedQueueIds.filter((id) => id !== queueId));
    } else {
      onChange([...selectedQueueIds, queueId]);
    }
  };

  const selectedQueues = queues.filter((q) => selectedQueueIds.includes(q.id));

  return (
    <div className="mt-4" ref={containerRef}>
      <label className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1">
        {i18n.t("queueSelect.inputLabel")}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={clsx(
            "w-full min-h-[42px] px-3 py-2 text-left text-sm border rounded-lg",
            "bg-white dark:bg-dark-800",
            "border-dark-300 dark:border-dark-600",
            "focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-primary-500 focus:ring-primary-500/20",
            "flex items-center justify-between gap-2"
          )}
        >
          <div className="flex flex-wrap gap-1.5 flex-1">
            {selectedQueues.length > 0 ? (
              selectedQueues.map((queue) => (
                <span
                  key={queue.id}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: queue.color }}
                >
                  {queue.name}
                </span>
              ))
            ) : (
              <span className="text-dark-400 dark:text-dark-500">
                Selecione as filas...
              </span>
            )}
          </div>
          <ChevronDownIcon
            className={clsx(
              "w-5 h-5 text-dark-400 transition-transform",
              isOpen && "transform rotate-180"
            )}
          />
        </button>

        {/* Dropdown - abre para cima */}
        {isOpen && (
          <div className="absolute z-50 w-full bottom-full mb-1 bg-white dark:bg-dark-800 border border-dark-200 dark:border-dark-700 rounded-lg shadow-lg max-h-60 overflow-auto">
            {queues.length === 0 ? (
              <div className="px-3 py-2 text-sm text-dark-500 dark:text-dark-400">
                Nenhuma fila disponível
              </div>
            ) : (
              queues.map((queue) => {
                const isSelected = selectedQueueIds.includes(queue.id);
                return (
                  <button
                    key={queue.id}
                    type="button"
                    onClick={() => handleToggleQueue(queue.id)}
                    className={clsx(
                      "w-full px-3 py-2 text-left text-sm flex items-center gap-3",
                      "hover:bg-dark-50 dark:hover:bg-dark-700",
                      "transition-colors"
                    )}
                  >
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: queue.color }}
                    />
                    <span className="flex-1 text-dark-700 dark:text-dark-300">
                      {queue.name}
                    </span>
                    {isSelected && (
                      <CheckIcon className="w-4 h-4 text-primary-500" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QueueSelect;
